import { useState, useEffect, useCallback } from 'react'
import { Link } from 'react-router-dom'
import {
  Shield, Cloud, Globe, Newspaper, Lightbulb, AlertTriangle,
  TrendingUp, Activity, Server, Bug, Radio,
  ArrowRight, RefreshCw, CheckCircle, XCircle, Target,
} from 'lucide-react'
import { clsx } from 'clsx'
import { formatDistanceToNow } from 'date-fns'
import { api } from '../lib/api'

const SEVERITY_COLORS: Record<string, string> = {
  critical: 'bg-rose text-white', high: 'bg-amber/20 text-amber-light',
  medium: 'bg-blue/20 text-blue-light', low: 'bg-emerald/20 text-emerald-light',
  info: 'bg-purple/20 text-purple-light',
}

const CATEGORY_ICONS: Record<string, React.ElementType> = {
  cyber: Shield, cloud: Cloud, world: Globe, innovation: Lightbulb, tech_news: Newspaper,
}

const CATEGORY_COLORS: Record<string, string> = {
  cyber: 'text-rose-light', cloud: 'text-cyan-light', world: 'text-amber-light',
  innovation: 'text-purple-light', tech_news: 'text-blue-light',
}

function relativeTime(ts: string): string {
  if (!ts) return ''
  try { return formatDistanceToNow(new Date(ts), { addSuffix: false }) + ' ago' } catch { return ts }
}

export default function DashboardPage() {
  const [threatLevel, setThreatLevel] = useState<any>(null)
  const [cloudStatus, setCloudStatus] = useState<any[]>([])
  const [signals, setSignals] = useState<any[]>([])
  const [news, setNews] = useState<any[]>([])
  const [trends, setTrends] = useState<any>(null)
  const [nbaActions, setNbaActions] = useState<any[]>([])
  const [loading, setLoading] = useState(true)

  const fetchAll = useCallback(async () => {
    setLoading(true)
    try {
      const [threatRes, cloudRes, signalsRes, newsRes, trendsRes, nbaRes] = await Promise.allSettled([
        api.get('/api/cyber/threat-level'),
        api.get('/api/cloud/status'),
        api.get('/api/signals/feed', { params: { limit: 20 } }),
        api.get('/api/news/feed', { params: { limit: 15 } }),
        api.get('/api/trends/live'),
        api.get('/api/next-best-action', { params: { limit: 5 } }),
      ])
      if (threatRes.status === 'fulfilled') setThreatLevel(threatRes.value.data)
      if (cloudRes.status === 'fulfilled') setCloudStatus(cloudRes.value.data.providers || [])
      if (signalsRes.status === 'fulfilled') setSignals(signalsRes.value.data.signals || [])
      if (newsRes.status === 'fulfilled') setNews(newsRes.value.data.articles || [])
      if (trendsRes.status === 'fulfilled') setTrends(trendsRes.value.data)
      if (nbaRes.status === 'fulfilled') setNbaActions(nbaRes.value.data.items || [])
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { fetchAll(); const i = setInterval(fetchAll, 60_000); return () => clearInterval(i) }, [fetchAll])

  const cloudIssues = cloudStatus.filter(p => p.overall_status !== 'operational')
  const cloudOk = cloudStatus.filter(p => p.overall_status === 'operational')
  const topCompanies = (trends?.trending_companies || []).slice(0, 8)
  const topTech = (trends?.trending_technologies || []).slice(0, 6)

  if (loading && !signals.length && !news.length) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw size={24} className="animate-spin text-text-muted" />
        <span className="ml-3 text-text-muted">Loading real-time intelligence...</span>
      </div>
    )
  }

  return (
    <div className="space-y-4 animate-fade-in">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-text-primary">Command Centre</h1>
          <p className="text-xs text-text-secondary">120+ feeds, 19 cloud providers, 80+ entities tracked</p>
        </div>
        <button type="button" onClick={fetchAll} className="btn-secondary text-xs">
          <RefreshCw size={14} className={loading ? 'animate-spin' : ''} /> Refresh
        </button>
      </div>

      {/* AI Value Margin strip — projected annual saving + FTE-equivalent, at a glance */}
      <SavingsStrip />

      {/* ══ Next Best Actions — top 5, the at-a-glance "what to do now" view ═══ */}
      <div className="card p-4 border-l-2 border-cyan/40">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-xs font-semibold text-text-primary flex items-center gap-2">
            <Target size={14} className="text-cyan" /> Next Best Actions
            <span className="text-[10px] font-normal text-text-muted">— ranked by urgency, time-criticality wins</span>
          </h2>
          <Link to="/app/next-best-action" className="text-[10px] text-blue-light hover:underline inline-flex items-center gap-0.5">
            see all <ArrowRight size={11} />
          </Link>
        </div>
        {nbaActions.length === 0 ? (
          <div className="text-[11px] text-text-muted py-2">Nothing urgent right now — you're on top of it.</div>
        ) : (
          <div className="space-y-1.5">
            {nbaActions.slice(0, 5).map((a: any, i: number) => {
              const tc = String(a.time_criticality || '').toLowerCase()
              const imminent = /\b\d+h\b|<1h|<1d|underway|overdue|expired|imminent|negative/.test(tc)
              return (
                <div key={a.id} className="flex items-center gap-2 text-[11px]">
                  <span className="w-4 text-text-muted font-bold text-center flex-shrink-0">{a.rank ?? i + 1}</span>
                  <span className="text-[9px] px-1 py-0.5 rounded bg-surface-raised border border-border text-text-muted whitespace-nowrap flex-shrink-0">{a.category}</span>
                  <span className="text-text-secondary truncate flex-1">{a.title}</span>
                  <span className={clsx(
                    'text-[9px] px-1 py-0.5 rounded whitespace-nowrap flex-shrink-0 border',
                    imminent ? 'border-rose/50 bg-rose/15 text-rose-light font-semibold' : 'border-border bg-surface-raised text-text-muted',
                  )}>{a.time_criticality}</span>
                  <Link to={a.deep_link || '/app/next-best-action'}
                        className="text-cyan-light hover:underline whitespace-nowrap flex-shrink-0 inline-flex items-center gap-0.5">
                    Act <ArrowRight size={10} />
                  </Link>
                </div>
              )
            })}
          </div>
        )}
      </div>

      {/* ══ ROW 1: KPIs ═══════════════════════════════════════════════════════ */}
      <div className="grid grid-cols-3 sm:grid-cols-5 lg:grid-cols-7 gap-2">
        <div className="stat-card py-3 px-4">
          <span className="stat-label text-[10px]">Threat Level</span>
          <span className={clsx('text-lg font-bold', threatLevel?.level === 'critical' ? 'text-rose' : threatLevel?.level === 'elevated' ? 'text-amber' : 'text-emerald')}>
            {(threatLevel?.level || '—').toUpperCase()}
          </span>
        </div>
        <div className="stat-card py-3 px-4">
          <span className="stat-label text-[10px]">Critical CVEs</span>
          <span className="text-lg font-bold text-text-primary">{threatLevel?.critical_cves_7d ?? '—'}</span>
        </div>
        <div className="stat-card py-3 px-4">
          <span className="stat-label text-[10px]">Exploited</span>
          <span className="text-lg font-bold text-text-primary">{threatLevel?.kev_additions_7d ?? '—'}</span>
        </div>
        <div className="stat-card py-3 px-4">
          <span className="stat-label text-[10px]">Cloud OK</span>
          <span className="text-lg font-bold text-emerald">{cloudOk.length}</span>
        </div>
        <div className="stat-card py-3 px-4">
          <span className="stat-label text-[10px]">Cloud Issues</span>
          <span className={clsx('text-lg font-bold', cloudIssues.length > 0 ? 'text-rose' : 'text-emerald')}>{cloudIssues.length}</span>
        </div>
        <div className="stat-card py-3 px-4">
          <span className="stat-label text-[10px]">Signals</span>
          <span className="text-lg font-bold text-text-primary">{signals.length}</span>
        </div>
        <div className="stat-card py-3 px-4">
          <span className="stat-label text-[10px]">Articles (DB)</span>
          <span className="text-lg font-bold text-text-primary">{trends?.total_articles_scanned || '—'}</span>
        </div>
      </div>

      {/* ══ ROW 2: Cloud Status Bar + Threat Summary ══════════════════════════ */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Cloud mini-status */}
        <div className="card p-4">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-xs font-semibold text-text-primary flex items-center gap-2">
              <Cloud size={14} className="text-cyan" /> Cloud Status ({cloudStatus.length} providers)
            </h2>
            <Link to="/app/cloud" className="text-[10px] text-blue-light hover:underline">View all</Link>
          </div>
          <div className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-5 gap-1">
            {cloudStatus.map(p => {
              const isOk = p.overall_status === 'operational'
              return (
                <div key={p.slug} className={clsx('flex items-center gap-1.5 px-2 py-1 rounded text-[10px]', isOk ? 'text-text-muted' : 'text-rose-light bg-rose/5')}>
                  <span className={clsx('w-1.5 h-1.5 rounded-full shrink-0', isOk ? 'bg-emerald' : p.overall_status === 'outage' ? 'bg-rose' : 'bg-amber')} />
                  <span className="truncate">{p.name?.replace('Amazon Web Services', 'AWS').replace('Google Cloud Platform', 'GCP').replace('Microsoft ', 'MS ').replace('Oracle Cloud (OCI)', 'Oracle')}</span>
                </div>
              )
            })}
          </div>
        </div>

        {/* Threat summary */}
        {threatLevel && (
          <div className={clsx('card p-4 border', threatLevel.level === 'critical' ? 'border-rose/30 bg-rose/5' : threatLevel.level === 'elevated' ? 'border-amber/30 bg-amber/5' : 'border-emerald/30 bg-emerald/5')}>
            <div className="flex items-center gap-2 mb-2">
              <Shield size={16} className={threatLevel.level === 'critical' ? 'text-rose' : threatLevel.level === 'elevated' ? 'text-amber' : 'text-emerald'} />
              <h2 className={clsx('text-xs font-semibold', threatLevel.level === 'critical' ? 'text-rose-light' : threatLevel.level === 'elevated' ? 'text-amber-light' : 'text-emerald-light')}>
                Threat Level: {threatLevel.level.toUpperCase()}
              </h2>
              <Link to="/app/cyber" className="text-[10px] text-blue-light hover:underline ml-auto">Cyber Dashboard</Link>
            </div>
            <p className="text-[11px] text-text-secondary">{threatLevel.summary}</p>
          </div>
        )}
      </div>

      {/* ══ ROW 3: Signal Feed + Trending ═════════════════════════════════════ */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Signal Feed */}
        <div className="card p-4 lg:col-span-2">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-xs font-semibold text-text-primary flex items-center gap-2">
              <Radio size={14} className="text-amber" /> Live Signal Feed
            </h2>
            <Link to="/app/signals" className="text-[10px] text-blue-light hover:underline">View all</Link>
          </div>
          <div className="space-y-1 max-h-[350px] overflow-y-auto">
            {signals.map((signal, idx) => {
              const Icon = CATEGORY_ICONS[signal.category] || Activity
              const catColor = CATEGORY_COLORS[signal.category] || 'text-text-muted'
              return (
                <div key={idx} className="flex items-start gap-2 py-1.5 border-b border-border last:border-0">
                  <Icon size={13} className={clsx('mt-0.5 shrink-0', catColor)} />
                  <div className="flex-1 min-w-0">
                    <p className="text-[11px] text-text-primary leading-snug truncate">
                      {signal.url ? <a href={signal.url} target="_blank" rel="noopener" className="hover:underline">{signal.title}</a> : signal.title}
                    </p>
                    <div className="flex items-center gap-1.5 mt-0.5">
                      <span className={clsx('badge text-[9px] py-0', SEVERITY_COLORS[signal.severity] || 'bg-surface-hover text-text-muted')}>{signal.severity}</span>
                      <span className="text-[9px] text-text-muted">{signal.source}</span>
                      <span className="text-[9px] text-text-muted">{relativeTime(signal.timestamp)}</span>
                    </div>
                  </div>
                </div>
              )
            })}
            {signals.length === 0 && <p className="text-xs text-text-muted py-4 text-center">Loading signals...</p>}
          </div>
        </div>

        {/* Trending */}
        <div className="card p-4">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-xs font-semibold text-text-primary flex items-center gap-2">
              <TrendingUp size={14} className="text-emerald" /> Trending
            </h2>
            <Link to="/app/trends" className="text-[10px] text-blue-light hover:underline">View all</Link>
          </div>
          <div className="mb-3">
            <h3 className="text-[10px] text-text-muted uppercase tracking-wider mb-1">Companies</h3>
            {topCompanies.map((c: any, i: number) => (
              <div key={c.name} className="flex items-center justify-between py-0.5 text-[11px]">
                <span className="text-text-primary truncate">{c.name}</span>
                <span className="font-mono text-text-muted text-[10px]">{c.mentions}</span>
              </div>
            ))}
            {topCompanies.length === 0 && <p className="text-[10px] text-text-muted">Loading...</p>}
          </div>
          <div>
            <h3 className="text-[10px] text-text-muted uppercase tracking-wider mb-1">Technologies</h3>
            {topTech.map((t: any, i: number) => (
              <div key={t.name} className="flex items-center justify-between py-0.5 text-[11px]">
                <span className="text-text-primary truncate">{t.name}</span>
                <span className="font-mono text-text-muted text-[10px]">{t.mentions}</span>
              </div>
            ))}
            {topTech.length === 0 && <p className="text-[10px] text-text-muted">Loading...</p>}
          </div>
        </div>
      </div>

      {/* ══ ROW 4: Latest News + Quick Actions ════════════════════════════════ */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Latest news */}
        <div className="card p-4 lg:col-span-2">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-xs font-semibold text-text-primary flex items-center gap-2">
              <Newspaper size={14} className="text-blue" /> Latest Tech News
            </h2>
            <Link to="/app/tech-news" className="text-[10px] text-blue-light hover:underline">View all</Link>
          </div>
          <div className="space-y-1 max-h-[250px] overflow-y-auto">
            {news.map((article, idx) => (
              <div key={idx} className="flex items-start gap-2 py-1.5 border-b border-border last:border-0">
                <div className="flex-1 min-w-0">
                  <p className="text-[11px] text-text-primary leading-snug">
                    {article.url ? <a href={article.url} target="_blank" rel="noopener" className="hover:underline">{article.title}</a> : article.title}
                  </p>
                  <span className="text-[9px] text-text-muted">{article.source} &middot; {relativeTime(article.timestamp)}</span>
                </div>
              </div>
            ))}
            {news.length === 0 && <p className="text-xs text-text-muted py-4 text-center">Loading news...</p>}
          </div>
        </div>

        {/* Quick Actions */}
        <div className="card p-4">
          <h2 className="text-xs font-semibold text-text-primary mb-3">Quick Actions</h2>
          <div className="space-y-1">
            {[
              { to: '/app/cyber', label: 'Cyber Security', icon: Shield, color: 'text-rose-light' },
              { to: '/app/cloud', label: 'Cloud Status', icon: Cloud, color: 'text-cyan-light' },
              { to: '/app/world', label: 'World Monitor', icon: Globe, color: 'text-amber-light' },
              { to: '/app/trends', label: 'Trend Mining', icon: TrendingUp, color: 'text-emerald-light' },
              { to: '/app/hype-cycle', label: 'Hype Cycle', icon: Activity, color: 'text-purple-light' },
              { to: '/app/competitors', label: 'Competitor Intel', icon: Server, color: 'text-blue-light' },
              { to: '/app/incidents', label: 'Incidents', icon: AlertTriangle, color: 'text-rose-light' },
              { to: '/app/briefing', label: 'Daily Briefing', icon: Newspaper, color: 'text-blue-light' },
            ].map(action => (
              <Link key={action.to} to={action.to} className="flex items-center gap-2 px-2 py-1.5 rounded-lg hover:bg-surface-hover transition-colors group">
                <action.icon size={13} className={action.color} />
                <span className="text-[11px] text-text-secondary group-hover:text-text-primary">{action.label}</span>
                <ArrowRight size={10} className="ml-auto text-text-muted opacity-0 group-hover:opacity-100" />
              </Link>
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}

/** Headcount-avoided strip on the Overview page, linking to /app/savings.
 *  Hides when < 1 FTE — the bare hours number isn't yet board-room news,
 *  no need to occupy hero real estate on Overview. */
function SavingsStrip() {
  const [data, setData] = useState<any | null>(null)
  useEffect(() => {
    api.get('/api/savings/summary')
      .then(r => setData(r.data))
      .catch(() => setData(null))
  }, [])
  if (!data) return null
  const fte = data.fte_equivalent ?? 0
  if (fte < 1) return null  // hide until we cross 1 whole FTE
  const annualH = Math.round(data.annual_rate_hours ?? 0)
  const ytdH    = Math.round(data.ytd?.hours ?? 0)
  const monthH  = Math.round(data.month?.hours ?? 0)
  return (
    <Link to="/app/savings"
      className="card p-4 bg-gradient-to-r from-emerald/10 via-emerald/5 to-surface-card border-emerald/25 hover:border-emerald/50 transition-colors block">
      <div className="flex flex-wrap items-center gap-x-8 gap-y-2">
        <div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-emerald-light font-semibold">Headcount avoided · 12-month projection</div>
          <div className="flex items-baseline gap-3 mt-1">
            <span className="text-3xl font-black font-mono text-emerald tabular-nums">{fte.toFixed(2)}</span>
            <span className="text-xs text-text-muted">Digital FTEs · {annualH.toLocaleString('en-GB')} h / year</span>
          </div>
        </div>
        <div className="ml-auto flex gap-6">
          <div>
            <div className="text-[9px] uppercase tracking-widest text-text-muted">This month</div>
            <div className="text-xl font-black font-mono text-text-primary">{monthH.toLocaleString('en-GB')} h</div>
          </div>
          <div>
            <div className="text-[9px] uppercase tracking-widest text-text-muted">YTD</div>
            <div className="text-xl font-black font-mono text-text-primary">{ytdH.toLocaleString('en-GB')} h</div>
          </div>
        </div>
      </div>
    </Link>
  )
}
